import { z } from '@campai/lib-common/utils/zod'

import { login } from './loginLogout'
import { clearSession, type Session } from './session'

import type { AuthSettings } from './types'

export const recoverProviderSession = async (authSettings: AuthSettings, session: Session): Promise<boolean> => {
    const { issuer, legacyIssuer, clientId, getSession } = authSettings
    if (!legacyIssuer || legacyIssuer === issuer || getSession) {
        return false
    }

    let sessionIssuer: unknown
    try {
        // The stored issuer only identifies migration candidates; OAuth still verifies the new session.
        const payload = session.idToken.split('.')[1].replaceAll('-', '+').replaceAll('_', '/')
        sessionIssuer = JSON.parse(atob(payload)).iss
    } catch {
        return false
    }

    if (sessionIssuer !== legacyIssuer) {
        return false
    }

    clearSession(authSettings)

    if (!session.refreshToken) {
        return true
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 10_000)
    let handoffTicket: string

    try {
        const response = await fetch(`${issuer.replace(/\/+$/, '')}/auth/session/recover`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ clientId, legacyRefreshToken: session.refreshToken }),
            signal: controller.signal,
        })

        if (!response.ok) {
            return true
        }

        const result = z.object({ ticket: z.string().min(1) }).safeParse(await response.json())
        if (!result.success) {
            return true
        }
        handoffTicket = result.data.ticket
    } catch {
        // Recovery is a single attempt; failures fall back to normal sign-in.
        return true
    } finally {
        clearTimeout(timeout)
    }

    await login(authSettings)({ returnTo: window.location.href, handoffTicket })

    // Navigation replaces this page; do not initialize the app with the old tokens while it is pending.
    return new Promise<never>(() => {})
}
